import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useHistory, useLocation } from 'react-router-dom'
import { connect } from 'react-redux'
import useCopyToClipboard from 'hooks/useCopyToClipboard'
import * as Analytics from 'analytics/analytics'
import { promotionClickEvent, promotionViewEvent } from 'analytics/snowplow/events'
import PromoDisplayModel from './PromoDisplayModel'
import { getReferrerNameFromReferralCode } from 'marketing/shared/getReferrerNameFromReferralCode'
import { fetchReferralValue } from 'actions/ReferAction'
import { useAppDispatch } from 'hooks/reduxHooks'
import { getMaxRefereeProductTypeDiscount } from 'marketing/shared/getMaxRefereeProductTypeDiscount'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'

interface Props {
  memberId: string
  numberOfPurchases: number
  regionCode: string
  maxRefereeAmount: number | null
}

function ReferralCodePrompt(props: Props) {
  const history = useHistory()
  const location = useLocation()
  const promptRef = useRef<HTMLDivElement>(null)
  const { regionCode, memberId, numberOfPurchases, maxRefereeAmount } = props
  const [isCopied, setIsCopied] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const [, copy] = useCopyToClipboard()

  const [referralCode, setReferralCode] = useState<string | null>(null)

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const code = urlParams.get('referralCode')
    if (code) {
      setReferralCode(code)
      urlParams.delete('referralCode')
      const query = urlParams.toString()
      history.replace(query ? `${location.pathname}?${query}` : location.pathname)
    }
  }, [location.pathname, location.search, history])

  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchReferralValue(regionCode))
  }, [dispatch, regionCode])

  const shouldShowReferralCode = useMemo(() => {
    if (memberId && numberOfPurchases > 0) return false
    if (!referralCode) return false

    // Avoid accessing sessionStorage during SSR
    const shownCodes = typeof window !== 'undefined' ? sessionStorage.getItem('shownReferralCodes') : null
    if (shownCodes) {
      const codes = JSON.parse(shownCodes)
      if (codes.includes(referralCode)) return false
    }

    return true
  }, [memberId, numberOfPurchases, referralCode])

  const referrerName = useMemo(() => getReferrerNameFromReferralCode({ referralCode }), [referralCode])

  useEffect(() => {
    if (shouldShowReferralCode && referralCode && typeof window !== 'undefined') {
      const shownCodes = sessionStorage.getItem('shownReferralCodes') || '[]'
      const codes = JSON.parse(shownCodes)
      if (!codes.includes(referralCode)) {
        codes.push(referralCode)
        sessionStorage.setItem('shownReferralCodes', JSON.stringify(codes))
      }
    }
  }, [shouldShowReferralCode, referralCode])

  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => {
        setIsCopied(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [isCopied])

  useEffect(() => {
    if (shouldShowReferralCode && referralCode) {
      Analytics.trackEvent(promotionViewEvent({
        promotionContext: { promotionName: 'referral_code', promotionType: 'prompt' },
        promotionCodeContext: { codeName: referralCode },
      }))
    }
  }, [shouldShowReferralCode, referralCode])

  const intl = useIntl()
  const currencyFormatter = useCurrencyFormatter()
  const formattedMaxRefereeAmount = useMemo(() => {
    return maxRefereeAmount ? currencyFormatter(maxRefereeAmount) : undefined
  }, [maxRefereeAmount, currencyFormatter])

  const fallbackName = intl.formatMessage({
    id: 'PzQCEfr7XYv7eeNB8xSW',
    defaultMessage: 'Someone',
    description: 'Fallback display name when referrer name is unavailable in referral prompt',
  })
  const fallbackPossessive = intl.formatMessage({
    id: 'KsBfUTJjSgnlRg5J1XQf',
    defaultMessage: 'their',
    description: 'Fallback possessive pronoun when referrer name is unavailable in referral prompt',
  })
  const fallbackObjective = intl.formatMessage({
    id: 'AQp0zY-Gi6ieTXWu0Noz',
    defaultMessage: 'them',
    description: 'Fallback objective pronoun when referrer name is unavailable in referral prompt',
  })

  const headerText = intl.formatMessage(
    {
      id: 'tbWOWfKkon7YQbwC_L93',
      defaultMessage: '{referrerName} has shared their referral code with you!',
      description: 'Header text for referral code prompt showing who shared the code',
    },
    { referrerName: referrerName || fallbackName },
  )

  const bodyText = useMemo(() => {
    if (formattedMaxRefereeAmount) {
      return intl.formatMessage(
        {
          id: 'TAAv2ZaQxnS56eSUv-Ja',
          defaultMessage: 'Use {referrerName} referral code to earn {amount} off your first booking while you help {referrerObjective} unlock special benefits as well!',
          description: 'Body text for referral code prompt with discount amount',
        },
        { referrerName: referrerName || fallbackPossessive, amount: formattedMaxRefereeAmount, referrerObjective: referrerName || fallbackObjective },
      )
    }
    return intl.formatMessage(
      {
        id: 'BnipYvQ3dVffi6z8I_wr',
        defaultMessage: 'Use {referrerName} referral code to unlock special benefits for {referrerObjective} as well!',
        description: 'Body text for referral code prompt without discount amount',
      },
      { referrerName: referrerName || fallbackPossessive, referrerObjective: referrerName || fallbackObjective },
    )
  }, [referrerName, formattedMaxRefereeAmount, intl, fallbackPossessive, fallbackObjective])

  const onClickCopyIcon = useCallback(() => {
    if (referralCode) {
      copy(referralCode)
      setIsCopied(true)
      Analytics.trackEvent(promotionClickEvent({
        promotionContext: { promotionName: 'referral_code', promotionType: 'prompt' },
        promotionCodeContext: { codeName: referralCode },
      }))
    }
  }, [referralCode, copy])

  const onClose = useCallback(() => {
    setIsVisible(false)
  }, [])

  if (!shouldShowReferralCode || !isVisible) {
    return null
  }

  return (
    <PromoDisplayModel
      show
      promptRef={promptRef}
      HeaderIcon={<div>🎁</div>}
      codeName={referralCode || undefined}
      expiresAt={undefined}
      bodyText={bodyText}
      headerText={headerText}
      displayMode="appear_once"
      isCopied={isCopied}
      onClose={onClose}
      onClickCopyIcon={onClickCopyIcon}
    />
  )
}

const mapState = (state: App.State) => {
  return {
    memberId: state.auth.account.memberId,
    regionCode: state.geo.currentRegionCode,
    maxRefereeAmount: state?.refer?.referralValue?.product_type_discount ? getMaxRefereeProductTypeDiscount(state.refer.referralValue.product_type_discount) : null,
    // WTF SVC-AUTH RETURNS NULL
    numberOfPurchases: state.auth.account.numberOfPurchases || 0,
  }
}

export default connect(mapState)(memo(ReferralCodePrompt))
